import { MainApi } from 'api/MainApi'
import type { GausiumExecutableTaskRecent } from 'api/RobotApi'
import clsx from 'clsx'
import ActionsMenu, { ActionsMenuItem } from 'components/ActionsMenu/ActionsMenu'
import ConfirmDialog from 'components/ConfirmDialog/ConfirmDialog'
import TextFieldDescribed from 'components/TextFieldDescribed/TextFieldDescribed'
import { useIsMobile } from 'hooks/useIsMobile'
import { ReactComponent as RemoveSvg } from 'images/icons/delete.svg'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import { PATH } from 'utils/constants'
import getTestId from 'utils/getTestId'
import { getRobotTypeDisplayName } from 'utils/robotTypeDisplayName'
import { getScenarioLaunchBlockReasonTitle } from 'utils/scenarioReadiness'
import { VENDOR } from 'utils/vendorDisplayName'

import style from './SharedScenarioItem.module.css'

/** Scenarios table only: Gausium map rows show type without the "Gausium "/"GS " prefix. */
function scenarioGausiumRobotTypeLabel(typeCode: string | null | undefined): string {
  const full = getRobotTypeDisplayName(VENDOR.GAUSIUM, typeCode)
  return full.replace(/^(Gausium|GS)\s+/i, '').trim() || full
}

export interface IScenarioInfo {
  id: number
  name: string
  missions_ids: Array<number | string>
  missions_count: number
  missions_statuses: string[]
  robot_type?: string
  missions_type_inconsistent?: boolean
  /** True when maintenance.service_points has a row with empty action_type (not launchable). */
  service_points_incomplete?: boolean
  is_gausium_map?: boolean
  /** GS: row is mirrored executable task (launch uses task_name + map_name). */
  is_gausium_executable_task?: boolean
  gausium_robot_id?: number
  /** GausiumRobotMap.id for map preview / geo pin */
  gausium_map_pk?: number | null
  gausium_exec_task?: GausiumExecutableTaskRecent
  /** PNG URL from Gausium map sync; scenarios list only */
  preview_url?: string | null
}

export interface ISharedScenarioItemProps {
  scenario: IScenarioInfo
  highlighted?: boolean
  onClick?: () => void
  onLaunch?: (scenario: IScenarioInfo) => void
  onScenariosUpdated: () => void
  hideIdColumn?: boolean
  hideRobotColumn?: boolean
  hideDelete?: boolean
  showExclude?: boolean
}

const SharedScenarioItem = ({
  scenario,
  highlighted,
  onClick,
  onLaunch,
  onScenariosUpdated,
  hideIdColumn = false,
  hideRobotColumn = false,
  hideDelete,
  showExclude = false,
}: ISharedScenarioItemProps) => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const [deleteDialog, setDeleteDialog] = useState(false)
  const [renameDialog, setRenameDialog] = useState(false)
  const [renameName, setRenameName] = useState(scenario.name)

  const goToScenario = () => navigate(`${PATH.SCENARIOS}${scenario.id}`)

  const handleRowClick = () => {
    onClick?.()
  }

  const copyScenario = async () => {
    try {
      await MainApi.copyScenario(scenario.id)
      onScenariosUpdated()
    } catch (error) {
      console.error('Error copying scenario:', error)
    }
  }

  const renameScenario = async () => {
    try {
      await MainApi.patchScenarios(scenario.id, { name: renameName })
      setRenameDialog(false)
      onScenariosUpdated()
    } catch (error) {
      console.error('Error renaming scenario:', error)
    }
  }

  const deleteScenario = async () => {
    try {
      await MainApi.deleteScenario(scenario.id)
      setDeleteDialog(false)
      onScenariosUpdated()
    } catch (error) {
      console.error('Error deleting scenario:', error)
    }
  }

  const excludeScenario = async () => {
    try {
      await MainApi.patchScenarios(scenario.id, { facility_id: null as any })
      onScenariosUpdated()
    } catch (error) {
      console.error('Error excluding scenario:', error)
    }
  }

  const isMobile = useIsMobile()
  const isScenarioError =
    scenario.missions_statuses?.length === 0 ||
    scenario.missions_statuses?.some(status => status !== 'ok') ||
    scenario.missions_type_inconsistent ||
    Boolean(scenario.service_points_incomplete)
  const launchBlockTitle = getScenarioLaunchBlockReasonTitle(scenario)

  const robotTypeCode = (scenario.robot_type || '').trim()
  const robotTypeLabel =
    scenario.is_gausium_map || scenario.is_gausium_executable_task
      ? scenarioGausiumRobotTypeLabel(scenario.robot_type)
      : getRobotTypeDisplayName(VENDOR.AUTONOMICS, robotTypeCode) || robotTypeCode

  const isReadonly = !!(scenario.is_gausium_map || scenario.is_gausium_executable_task)

  return (
    <>
      <div
        className={clsx(style.scenarioRow, {
          [style.highlighted]: highlighted,
          [style.errorRow]: isScenarioError,
          [style.scenarioRowNoId]: hideIdColumn && !hideRobotColumn,
          [style.scenarioRowNoRobot]: !hideIdColumn && hideRobotColumn,
          [style.scenarioRowNoIdNoRobot]: hideIdColumn && hideRobotColumn,
        })}
        title={launchBlockTitle}
        onClick={handleRowClick}
      >
        <span></span>
        {!hideIdColumn && <span className={style.colLeft}>{scenario.id}</span>}
        <span className={style.colLeftMultiline} title={scenario.name}>
          {scenario.name}
        </span>
        <span className={style.colCenterMultiline}>{scenario.missions_ids?.join(', ') || '—'}</span>
        {!hideRobotColumn && (
          <span className={style.colCenterMultiline}>
            {scenario.is_gausium_map || scenario.is_gausium_executable_task
              ? robotTypeLabel || '—'
              : robotTypeCode && robotTypeCode !== 'Unknown'
              ? robotTypeLabel
              : '—'}
          </span>
        )}
        <span className={style.colCenter} onClick={e => e.stopPropagation()}>
          <ActionsMenu>
            <ActionsMenuItem
              onClick={() => onLaunch?.(scenario)}
              data-test-id={getTestId('launch-scenario')}
              disabled={!scenario.is_gausium_map && !scenario.is_gausium_executable_task && isScenarioError}
              title={
                !scenario.is_gausium_map && !scenario.is_gausium_executable_task && isScenarioError
                  ? launchBlockTitle
                  : undefined
              }
            >
              <span className={style.menuItemIcon} />
              <span className={style.menuItemText}>Launch</span>
            </ActionsMenuItem>
            {!isReadonly && (
              <>
                <ActionsMenuItem onClick={goToScenario} data-test-id={getTestId('edit-scenario')}>
                  <span className={style.menuItemIcon} />
                  <span className={style.menuItemText} data-test-id={getTestId('edit-title')}>
                    {t('common.edit')}
                  </span>
                </ActionsMenuItem>
                <ActionsMenuItem
                  onClick={() => {
                    setRenameName(scenario.name)
                    setRenameDialog(true)
                  }}
                  data-test-id={getTestId('rename-scenario')}
                >
                  <span className={style.menuItemIcon} />
                  <span className={style.menuItemText}>{t('common.rename')}</span>
                </ActionsMenuItem>
                <ActionsMenuItem onClick={copyScenario} data-test-id={getTestId('copy-scenario')}>
                  <span className={style.menuItemIcon} />
                  <span className={style.menuItemText}>{t('common.copy')}</span>
                </ActionsMenuItem>
                {!hideDelete && (
                  <ActionsMenuItem onClick={() => setDeleteDialog(true)} data-test-id={getTestId('remove-scenario')}>
                    <RemoveSvg className={style.menuItemIcon} />
                    <span className={style.menuItemText} data-test-id={getTestId('delete-title')}>
                      {t('common.delete')}
                    </span>
                  </ActionsMenuItem>
                )}
                {showExclude && (
                  <ActionsMenuItem onClick={excludeScenario} data-test-id={getTestId('exclude-scenario')}>
                    <RemoveSvg className={style.menuItemIcon} />
                    <span className={style.menuItemText} data-test-id={getTestId('delete-title')}>
                      {t('common.exclude')}
                    </span>
                  </ActionsMenuItem>
                )}
              </>
            )}
          </ActionsMenu>
        </span>
      </div>

      {!isReadonly && (
        <>
          <ConfirmDialog
            title="Rename the scenario"
            isOpen={renameDialog}
            onClose={() => setRenameDialog(false)}
            onConfirmClick={renameScenario}
            confirmText={t('common.save')}
            confirmButtonDisabled={!renameName.trim()}
          >
            <TextFieldDescribed
              description={undefined}
              placeholder="Scenario name"
              value={renameName}
              onChange={e => setRenameName(e.target.value)}
            />
          </ConfirmDialog>

          <ConfirmDialog
            title="Delete this scenario?"
            isOpen={deleteDialog}
            onClose={() => setDeleteDialog(false)}
            onConfirmClick={deleteScenario}
            confirmText={t('common.delete')}
            danger={true}
          />
        </>
      )}
    </>
  )
}

export default SharedScenarioItem
